import { logger } from '@/ui'
import { Button } from '@/ui/rapid-ui'
import { AlertCircleIcon, InfoIcon, Loader2Icon, SparklesIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function ConnectWhatsAppPanel({
	onActivate,
	isLoading,
	isActivatingAccount,
	unavailable = false,
	deactivatedDate = null,
}: {
	onActivate: () => void
	isLoading: boolean
	isActivatingAccount: boolean
	unavailable?: boolean
	deactivatedDate?: string | null
}) {
	const { t } = useTranslation('common')

	const handleActivate = () => {
		logger.track('whatsapp_onboarding_path_selected', {
			category: 'whatsapp',
			data: { path: 'jobilla_managed' },
		})
		onActivate()
	}

	return (
		<div className="space-y-3">
			<div className="bg-white border border-gray-200 rounded-xl p-5">
				<div className="flex items-start gap-4">
					<div className="size-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
						<SparklesIcon className="size-5 text-purple-600" />
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="font-medium text-gray-900">
							{t('whatsappSettings.activate.title')}
						</h3>
						<p className="mt-1 mb-4 text-sm text-gray-600">
							{t('whatsappSettings.activate.description')}
						</p>
						{deactivatedDate && (
							<p className="mb-4 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
								<InfoIcon className="size-4 flex-shrink-0" />
								{t('whatsappSettings.inactivity.deactivatedNotice', { date: deactivatedDate })}
							</p>
						)}
						{unavailable && (
							<p className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
								<AlertCircleIcon className="size-4 flex-shrink-0" />
								{t('whatsappSettings.activate.temporarilyUnavailable')}
							</p>
						)}
						<Button
							type="committal"
							onClick={handleActivate}
							disabled={unavailable || isLoading || isActivatingAccount}
						>
							{isActivatingAccount
								? (
									<span className="flex items-center gap-2">
										<Loader2Icon className="size-4 animate-spin" />
										{t('whatsappSettings.activate.activating')}
									</span>
								)
								: t('whatsappSettings.activate.cta')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export function FailedSignupPanel({
	onReset,
	isResetting,
}: {
	onReset: () => void
	isResetting: boolean
}) {
	const { t } = useTranslation('common')

	return (
		<div className="bg-red-50 border border-red-200 rounded-xl p-5">
			<div className="flex items-start gap-4">
				<div className="size-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
					<AlertCircleIcon className="size-5 text-red-600" />
				</div>
				<div className="flex-1">
					<h3 className="font-medium text-red-900 mb-1">
						{t('whatsappSettings.setupFailed')}
					</h3>
					<p className="text-sm text-red-700 mb-3">
						{t('whatsappSettings.setupFailedDescription')}
					</p>
					<Button
						type="subtle"
						onClick={onReset}
						disabled={isResetting}
					>
						{isResetting
							? (
								<span className="flex items-center gap-2">
									<Loader2Icon className="size-4 animate-spin" />
									{t('whatsappSettings.resetting')}
								</span>
							)
							: t('whatsappSettings.resetAndTryAgain')}
					</Button>
				</div>
			</div>
		</div>
	)
}
